import { useStore } from "../../app/stores/store";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { observer } from "mobx-react-lite";

import { cn } from "../../lib/utils";
import React, { useEffect, useRef } from "react";
import { all_routes } from "../router/all_routes";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";

// ✅ Utility function for clean route names
function formatName(name: string) {
  return name.toLowerCase().replace(/\s+/g, "-");
}

const Logo = () => (
  <div className="bg-white rounded-3xl py-2 px-4 flex items-center justify-center">
    <img src="/logo.svg" alt="Uber E-Visa Logo" width={120} height={24} />
  </div>
);

const Header = () => {
  const headerRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const pathname = location.pathname;
  const routes = all_routes;

  const {
    userStore: {
      user,
      isLoggedIn,
      logout,
      isAdmin,
      isCustomer,
      isPartner,
      isCashier,
      isAirportCashier,
    },
    categoryStore: { categories, loadCategories },
  } = useStore();

  // ✅ Animate header shrink on scroll
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const headerElement = headerRef.current;
    const containerElement = containerRef.current;
    if (!headerElement || !containerElement) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: document.documentElement,
        start: "top top",
        end: "+=100",
        scrub: 1,
      },
    });

    tl.to(headerElement, { top: "0", ease: "power1.inOut" }, 0).to(
      containerElement,
      {
        maxWidth: "100%",
        borderRadius: "0",
        paddingTop: "0.75rem",
        paddingBottom: "0.75rem",
        backgroundColor: "rgba(10, 10, 10, 0.95)",
        backdropFilter: "blur(8px)",
        ease: "power1.inOut",
      },
      0
    );

    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // ✅ Build navigation links dynamically from categories

  const navLinks = [
    { href: routes.homeOne, label: "Home" },
    ...(categories && categories.length > 0
      ? categories.map((category) => ({
          href: `/services/${formatName(category.name)}/${category.id}`,
          label: category.name,
        }))
      : []),

    { href: routes.aboutUs, label: "Who We Are" },
    { href: routes.contactUs, label: "Contact Us" },
  ];

  // ✅ Add dashboard links based on role
  const dashboardLinks = [];
  if (isLoggedIn) {
    if (isPartner())
      dashboardLinks.push({
        href: all_routes.PartnerDashboard,
        label: "Dashboard",
      });
    if (isCustomer())
      dashboardLinks.push({
        href: all_routes.Transactions,
        label: "Transactions",
      });
    if (isAdmin() || isCashier() || isAirportCashier())
      dashboardLinks.push({ href: all_routes.Dashboard, label: "Dashboard" });
  }

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header
      ref={headerRef}
      className="fixed top-4 left-0 right-0 z-50 flex justify-center"
    >
      <div
        ref={containerRef}
        className="bg-neutral-900/90 backdrop-blur-sm text-white rounded-full shadow-lg p-2 flex items-center justify-between w-full max-w-6xl"
      >
        {/* ✅ Logo */}
        <Link to="/" className="flex items-center gap-3 pl-2">
          <Logo />
        </Link>

        {/* ✅ Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 text-base font-medium">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.label}
                to={link.href}
                className={cn(
                  "transition-colors",
                  isActive
                    ? "text-primary font-semibold"
                    : "text-neutral-300 hover:text-white"
                )}
              >
                {link.label}
              </Link>
            );
          })}

          {dashboardLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={cn("transition-colors ")}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* ✅ Mobile Menu */}
        <div className="flex items-center gap-2">
          {!isLoggedIn ? (
            <Button
              variant="secondary"
              className="hidden sm:flex bg-white text-black hover:bg-neutral-200 rounded-full"
              asChild
            >
              <Link to={all_routes.login}>Login / Sign Up</Link>
            </Button>
          ) : (
            <>
              <span className="text-neutral-400 text-lg">
                {user?.displayName}
              </span>
              <Button
                variant="secondary"
                className="hidden sm:flex bg-white text-black hover:bg-neutral-200 rounded-full"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-white hover:bg-neutral-800 hover:text-white rounded-full"
              >
                <Menu />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>

            <SheetContent
              side="left"
              className="bg-neutral-900 text-white border-neutral-800 p-0"
            >
              <SheetHeader>
                <SheetTitle className="sr-only">Mobile Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-6 p-6">
                <Link to="/" className="flex items-center gap-3">
                  <Logo />
                </Link>
                <nav className="flex flex-col gap-4 mt-4">
                  {navLinks.map((link) => {
                    const isActive = pathname === link.href;
                    return (
                      <Link
                        key={link.label}
                        to={link.href}
                        className={cn(
                          "transition-colors text-xl",
                          isActive
                            ? "text-primary font-semibold"
                            : "text-neutral-300 hover:text-white"
                        )}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                  {dashboardLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="transition-colors text-xl"
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                {isLoggedIn ? (
                  <>
                    <span className="text-neutral-400 text-lg">
                      {user?.displayName}
                    </span>
                    <Button
                      onClick={handleLogout}
                      className="w-full bg-white text-black hover:bg-neutral-200 rounded-full mt-4"
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <Button
                    asChild
                    className="w-full bg-white text-black hover:bg-neutral-200 rounded-full mt-4"
                  >
                    <Link to="/login">Login / Sign Up</Link>
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default observer(Header);
